import { useState } from "react";
import { inspectionApi, type InspectionReadinessApi } from "@/lib/api";
import { hasPermission } from "@/lib/auth";
import type { ArborInspectionDetail } from "@/types/arboricultural-inspection";

interface Props {
  detail: ArborInspectionDetail;
  readiness: InspectionReadinessApi | null;
  /** Recarrega a vistoria depois de uma ação. */
  onChanged: () => void;
}

const STATUS_TEXT: Record<string, { label: string; tone: string }> = {
  DRAFT: { label: "Rascunho", tone: "bg-background-100 text-foreground-600" },
  IN_PROGRESS: { label: "Em preenchimento", tone: "bg-accent-50 text-accent-700" },
  SUBMITTED: { label: "Enviada, aguardando revisão", tone: "bg-warning-50 text-warning-700" },
  UNDER_REVIEW: { label: "Em revisão técnica", tone: "bg-warning-50 text-warning-700" },
  RETURNED: { label: "Devolvida para correção", tone: "bg-danger-50 text-danger-700" },
  VALIDATED: { label: "Validada", tone: "bg-primary-50 text-primary-700" },
  CONCLUDED: { label: "Concluída", tone: "bg-primary-50 text-primary-700" },
  CANCELLED: { label: "Cancelada", tone: "bg-background-100 text-foreground-500" },
};

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

type ReasonAction = "return" | "cancel";

export default function ReviewPanel({ detail, readiness, onChanged }: Props) {
  const status = detail.workflowStatus;
  const info = STATUS_TEXT[status] ?? { label: status, tone: "bg-background-100 text-foreground-600" };
  const canReview = hasPermission("inspection.homologate");
  const canCancel = hasPermission("inspection.update_own") || hasPermission("inspection.update_any");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reasonFor, setReasonFor] = useState<ReasonAction | null>(null);
  const [reason, setReason] = useState("");

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setReasonFor(null);
      setReason("");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível concluir a ação.");
    } finally {
      setBusy(false);
    }
  }

  const trail: Array<{ icon: string; text: string }> = [];
  if (detail.submittedAt)
    trail.push({ icon: "ri-send-plane-line", text: `Enviada por ${detail.submittedByName ?? "usuário não identificado"} em ${fmt(detail.submittedAt)}` });
  if (detail.returnedAt)
    trail.push({ icon: "ri-arrow-go-back-line", text: `Devolvida por ${detail.returnedByName ?? "usuário não identificado"} em ${fmt(detail.returnedAt)}` });
  if (detail.validatedAt)
    trail.push({ icon: "ri-shield-check-line", text: `Validada por ${detail.validatedByName ?? "usuário não identificado"} em ${fmt(detail.validatedAt)}` });
  if (detail.cancelledAt)
    trail.push({ icon: "ri-close-circle-line", text: `Cancelada por ${detail.cancelledByName ?? "usuário não identificado"} em ${fmt(detail.cancelledAt)}` });

  const validationIssues = readiness?.validation ?? [];
  const showValidationIssues = status === "UNDER_REVIEW" || status === "SUBMITTED";
  const cancellable = ["DRAFT", "IN_PROGRESS", "SUBMITTED", "RETURNED"].includes(status);

  return (
    <section className="bg-white border-b border-background-200/70 px-6 py-3 space-y-3" aria-label="Situação e revisão da vistoria">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${info.tone}`}>{info.label}</span>
        <div className="flex-1 min-w-0 flex flex-wrap gap-x-5 gap-y-1">
          {trail.map((t) => (
            <span key={t.text} className="text-xs text-foreground-500 flex items-center gap-1.5">
              <i className={t.icon}></i>
              {t.text}
            </span>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {status === "SUBMITTED" && canReview && (
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => inspectionApi.startReview(detail.id))}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
            >
              Iniciar revisão
            </button>
          )}
          {status === "UNDER_REVIEW" && canReview && (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => setReasonFor("return")}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-danger-300 text-danger-700 hover:bg-danger-50 disabled:opacity-50"
              >
                Devolver
              </button>
              <button
                type="button"
                disabled={busy || (readiness != null && !readiness.can_validate)}
                title={readiness != null && !readiness.can_validate ? "Há pendências que impedem a validação" : undefined}
                onClick={() => run(() => inspectionApi.validate(detail.id))}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Validar
              </button>
            </>
          )}
          {status === "VALIDATED" && canReview && (
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => inspectionApi.conclude(detail.id))}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
            >
              Concluir
            </button>
          )}
          {cancellable && canCancel && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setReasonFor("cancel")}
              className="px-3 py-1.5 text-xs font-medium rounded-lg text-foreground-500 hover:bg-background-100 disabled:opacity-50"
            >
              Cancelar vistoria
            </button>
          )}
        </div>
      </div>

      {status === "RETURNED" && detail.returnReason && (
        <div className="rounded-lg border border-danger-200 bg-danger-50/60 px-3 py-2.5 text-sm text-danger-800">
          <strong className="block text-xs uppercase tracking-wide text-danger-700 mb-0.5">Motivo da devolução</strong>
          {detail.returnReason}
          <p className="text-xs text-danger-700/80 mt-1">Corrija o que foi apontado e envie novamente.</p>
        </div>
      )}

      {status === "CANCELLED" && detail.cancelReason && (
        <div className="rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-600">
          <strong className="block text-xs uppercase tracking-wide text-foreground-500 mb-0.5">Motivo do cancelamento</strong>
          {detail.cancelReason}
        </div>
      )}

      {status === "SUBMITTED" && !canReview && (
        <p className="text-xs text-foreground-500">Aguardando um revisor técnico. Esta vistoria não pode ser editada enquanto estiver em revisão.</p>
      )}

      {showValidationIssues && validationIssues.length > 0 && canReview && (
        <ul className="rounded-lg border border-warning-200 bg-warning-50/60 px-4 py-2.5 text-xs text-warning-800 space-y-1 list-disc list-inside">
          {validationIssues.map((i) => (
            <li key={i.code}>{i.message}</li>
          ))}
        </ul>
      )}

      {error && <p className="text-xs text-danger-600">{error}</p>}

      {reasonFor && (
        <div className="rounded-lg border border-background-200 bg-background-50 p-3 space-y-2">
          <label className="block text-xs font-medium text-foreground-700">
            {reasonFor === "return" ? "Motivo da devolução (obrigatório)" : "Motivo do cancelamento (obrigatório)"}
          </label>
          <textarea
            rows={2}
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={reasonFor === "return" ? "O que precisa ser corrigido ou complementado." : "Por que esta vistoria não será concluída."}
            className="w-full bg-white border border-background-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-primary-300"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setReasonFor(null);
                setReason("");
              }}
              className="px-3 py-1.5 text-xs rounded-lg text-foreground-500 hover:bg-background-100"
            >
              Voltar
            </button>
            <button
              type="button"
              disabled={busy || reason.trim().length < 5}
              onClick={() =>
                run(() =>
                  reasonFor === "return"
                    ? inspectionApi.returnInspection(detail.id, { reason: reason.trim() })
                    : inspectionApi.cancel(detail.id, { reason: reason.trim() }),
                )
              }
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-danger-500 text-white hover:bg-danger-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {reasonFor === "return" ? "Devolver para correção" : "Cancelar vistoria"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
